import React, { useEffect, useState } from 'react'
import st from './EmployeeAnalytics.module.scss'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import useLocalization from 'hooks/useLocalization'
import { superAxiosRequest } from 'axiosApi'
import { useHistory, useLocation } from 'react-router-dom'
import EmployeeList from './EmployeeList/EmployeeList'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import { observer } from 'mobx-react'
import ActionMenu from 'components/UI/ActionMenu/ActionMenu'
import Pagination from '../../UI/Pagination/Pagination'

const initPagination = {
  page: 1,
  limit: 10,
}

const formatDate = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const EmployeeAnalytics = () => {
  const history = useHistory()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const [filter, updateFilter] = useLocalStorageFilter({})
  const [clients, setClients] = useState(null)
  const [pagination, setPagination] = useState(initPagination)
  const [loading, setLoading] = useState(false)
  const [funnels, setFunnels] = useState([])
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const funnelId = searchParams.get('funnel_id')

  const today = new Date()
  const thirtyDaysAgo = new Date(today)
  thirtyDaysAgo.setDate(today.getDate() - 30)

  const dateFromFormatted = formatDate(thirtyDaysAgo)
  const dateUntilFormatted = formatDate(today)

  useEffect(() => {
    let isMounted = true

    superAxiosRequest({
      link: 'integration/funnels',
      method: 'get',
      crm: true,
    })
      .then((res) => {
        if (isMounted) setFunnels(res.data)
      })
      .catch(() => {})
      .finally(() => {})

    updateFilter({
      ...filter,
      date_from: filter.date_from || dateFromFormatted,
      date_until: filter.date_until || dateUntilFormatted,
    })

    return () => {
      isMounted = false
    }
  }, [])

  const menuItems = funnels.map((funnel) => ({
    name: funnel.name,
    action: `navigate_to_funnel_${funnel.id}`,
  }))

  const fetchAnalytics = () => {
    if (funnelId) {
      setLoading(true)
      superAxiosRequest({
        method: 'get',
        link: 'reports/employee_analytics',
        crm: true,
        params: {
          ...filter,
          funnel: funnelId,
          offset: (pagination.page - 1) * pagination.limit,
          limit: pagination.limit,
        },
      })
        .then(({ data }) => {
          setClients(data)
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false)
        })
    }
  }

  useEffect(() => {
    if (funnelId) {
      setClients(null)
      fetchAnalytics()
    }
  }, [filter, funnelId, pagination])

  const { translate } = useLocalization({
    section: 'groups',
    subsection: 'all_groups',
    dict: {
      filter: 'Filter',
      search: 'Search',
      start_date: 'Start Date',
      end_date: 'End Date',
      reset_all: 'Reset All',
      apply: 'Apply',

      employee_analytics: 'Employee analytics',
      search_placeholder: 'Search',
      employee_full_name: 'Employee’s full name',
      deals_in_progress: 'Deals in progress',
      successful_deals: 'Successful deals',
      new: 'New',
      in_work: 'In work',
      successful: 'Successful',
      quantity: 'Quantity',
      sum: 'Sum',
      start_finish_date: 'Start & Finish Date',
      employee_full_name_field: 'Employee’s full name',
      search_client_name_placeholder: 'Search for client name',
      select_funnel_message: 'Please select a funnel to view analytics', // Добавлено новое сообщение
    },
  })

  const config = [
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: translate.start_finish_date,
    },
    {
      nameState: 'manager',
      sort_key: 'manager',
      placeholder: translate.search_client_name_placeholder,
      apiLinkCRM: 'integration/employees/?short_form=true',
      type: 'select-multi',
      label: translate.employee_full_name_field,
    },
  ]

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (
        location.pathname !== `/crm/employee-analytics/?funnel_id=${funnelId}`
      ) {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const createActionFunction = (funnelId) => {
    return () => {
      window.location.href = `/crm/employee-analytics/?funnel_id=${funnelId}`
    }
  }

  const actionFunctions = {}
  funnels.forEach((funnel) => {
    actionFunctions[`navigate_to_funnel_${funnel.id}`] = createActionFunction(
      funnel.id,
    )
  })

  const currentFunnel = funnels.find((f) => f.id === +funnelId)

  return (
    <div className={st.container}>
      <div className={st.header}>
        <div>
          <PageHeader title={translate.employee_analytics}></PageHeader>
          <div className={st.titleBlock}>
            <div className={st.positionSt}>
              <h1 className={st.title}>
                {currentFunnel ? currentFunnel.name : 'Выбрать воронку'}
              </h1>
              {funnels.length > 0 && (
                <ActionMenu
                  iconType={'arrow'}
                  menu={menuItems}
                  action={(actionName) => {
                    const actionFunction = actionFunctions[actionName]
                    if (actionFunction) {
                      actionFunction()
                    }
                  }}
                  right
                />
              )}
            </div>
          </div>
          <SearchBooks
            onSearch={(text) => {
              updateFilter({ ...filter, name: text })
              changeTextValue('name', {
                key: 'name',
                label: 'Search for client name',
                value: text,
              })
            }}
            onClear={() => {
              const { name: text, ...obj } = filter
              updateFilter(obj)
              setTextValues(
                filterTextValues.filter((field) => field.key !== 'name'),
              )
            }}
            value={filter?.name || ''}
            placeholder={translate.search_placeholder}
            translate={translate}
          />
        </div>
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            getFilterParams={(e) => {
              updateFilter(e)
            }}
            config={config}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
            translate={translate}
          />
        )}
      </div>

      {!funnelId ? (
        <div className={st.emptyState}>
          <p>
            {translate.select_funnel_message ||
              'Please select a funnel from the menu above to view analytics.'}
          </p>
        </div>
      ) : loading ? (
        <Loader />
      ) : (
        <div className={st.tableContainer}>
          <div className={st.flexTable}>
            <div className={st.flexRow}>
              <div className={`${st.flexColumn} ${st.firstColumn}`}>
                {translate.employee_full_name}
              </div>
              <div className={`${st.flexColumn} ${st.element}`}>
                {translate.deals_in_progress}
              </div>
              <div className={`${st.flexColumn} ${st.element}`}>
                {translate.successful_deals}
              </div>
              <div className={`${st.flexColumn} ${st.element}`} />
              <div className={`${st.flexColumn} ${st.element}`} />
            </div>
            {Array.isArray(clients) && clients.length > 0 ? (
              clients?.map((client, index) => (
                <EmployeeList key={client.id} client={client} index={index} />
              ))
            ) : (
              <div className={st.noData}>
                No data found for selected filters
              </div>
            )}
          </div>
        </div>
      )}

      <div className={st.pagination}>
        {clients?.length > 10 && (
          <Pagination
            limit={pagination.limit}
            count={clients?.length || 0}
            page={pagination.page}
            onChange={(page) => setPagination({ ...pagination, page })}
            onChangePerPage={(limit) => {
              setPagination({ page: 1, limit })
            }}
          />
        )}
      </div>
    </div>
  )
}

export default observer(EmployeeAnalytics)
